import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, Share2, MessageCircle, Mic, Users, Shield, TreePine, Plus, ArrowRight, User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MemorialLandingPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [memorials, setMemorials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
      // If logged in, fetch memorials
      const memorialsRes = await axios.get(`${API}/memorials/my`, { withCredentials: true });
      setMemorials(memorialsRes.data || []);
    } catch (error) {
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTribute = () => {
    if (currentUser) {
      navigate('/create-memorial');
    } else {
      sessionStorage.setItem('authRedirectPath', '/create-memorial');
      const redirectUrl = window.location.origin + '/home';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/memorial-tribute')}
              className="text-primary bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/family-tree')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* Right Side */}
          {currentUser ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {currentUser.picture ? (
                  <img src={currentUser.picture} alt={currentUser.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                )}
                <span className="text-sm font-sans text-foreground hidden sm:block">{currentUser.name}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/home'); }}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Button 
              onClick={handleStartTribute}
              data-testid="nav-start-tribute-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
            >
              Start a Tribute
            </Button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heart className="w-16 h-16 text-primary mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight text-foreground mb-6">
              {currentUser ? 'Your Memorial Tributes' : 'Honor Their Memory'}
            </h1>
            <p className="text-xl font-sans text-foreground-muted leading-relaxed mb-8 max-w-2xl mx-auto">
              {currentUser 
                ? 'Create a new tribute for a loved one or manage your existing memorial pages.'
                : 'Create a beautiful memorial tribute page for your loved one. Share memories, collect heartfelt messages, and preserve their legacy forever.'}
            </p>
            
            {currentUser ? (
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  onClick={() => navigate('/create-memorial')}
                  data-testid="hero-new-tribute-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-10 py-4 text-lg font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
                >
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Set Up Another Tribute Service
                </Button>
                {memorials.length > 0 && (
                  <Button 
                    onClick={() => {
                      const element = document.getElementById('your-tributes');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    variant="outline"
                    data-testid="hero-view-tributes-btn"
                    className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full px-10 py-4 text-lg font-serif tracking-wide transition-all"
                  >
                    Go to Existing Tributes
                    <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            ) : (
              <Button 
                onClick={handleStartTribute}
                data-testid="hero-start-tribute-btn"
                className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-10 py-4 text-lg font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
              >
                Set Up a Tribute Service for a Loved One
              </Button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Your Tributes Section (Logged in users only) */}
      {currentUser && memorials.length > 0 && (
        <section id="your-tributes" className="py-16 px-6 bg-paper">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-serif text-foreground mb-8">Your Memorial Tributes</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memorials.map(memorial => (
                <motion.div
                  key={memorial.memorial_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(`/tribute/${memorial.memorial_id}`)}
                  className="bg-white border border-border shadow-card rounded-xl overflow-hidden cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all"
                >
                  {memorial.deceased_photo && (
                    <div className="h-32 overflow-hidden">
                      <img 
                        src={memorial.deceased_photo} 
                        alt={memorial.deceased_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <Heart className="w-5 h-5 text-rose-500" strokeWidth={1.5} />
                      <h3 className="text-xl font-serif text-foreground">{memorial.deceased_name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {memorial.tribute_count || 0} tributes received
                    </p>
                    <span className="text-primary font-sans text-sm flex items-center">
                      View & Manage <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-20 px-6 bg-paper">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-serif tracking-tight text-center text-foreground mb-16">
            How Memorial Tributes Work
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Create the Tribute",
                description: "Add your loved one's photo, name, dates, and a personal message. Choose a service type like Memorial Service, Bhog and Ardas, or Tehrvi.",
                icon: Heart
              },
              {
                step: "2",
                title: "Share the Link",
                description: "Get a unique URL to share with family and friends. Anyone with the link can view the tribute page and add their own messages.",
                icon: Share2
              },
              {
                step: "3",
                title: "Collect Tributes",
                description: "Family and friends can leave text messages or voice recordings. View all tributes and a beautiful word cloud of memories.",
                icon: MessageCircle
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-serif mx-auto mb-6">
                  {item.step}
                </div>
                <item.icon className="w-10 h-10 text-primary mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-2xl font-serif text-foreground mb-3">{item.title}</h3>
                <p className="text-base font-sans text-foreground-muted leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-serif tracking-tight text-center text-foreground mb-16">
            Features for Hosts & Visitors
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Users,
                title: "Guest or Anonymous Tributes",
                description: "Visitors can add tributes as guests without creating an account. They can also choose to post anonymously."
              },
              {
                icon: Mic,
                title: "Voice Messages",
                description: "Record heartfelt voice messages that are collected in a dedicated audio section for everyone to hear."
              },
              {
                icon: Shield,
                title: "Host Controls",
                description: "As the host, you have full control. View a word cloud of all messages and delete any inappropriate content."
              },
              {
                icon: Share2,
                title: "Easy Sharing",
                description: "Share the unique tribute URL via WhatsApp, email, or any messaging app to invite family and friends."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-border shadow-card rounded-lg p-6 flex items-start space-x-4"
              >
                <feature.icon className="w-8 h-8 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-xl font-serif text-foreground mb-2">{feature.title}</h3>
                  <p className="text-base font-sans text-foreground-muted leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-20 px-6 bg-paper">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif tracking-tight text-foreground mb-8">
            Customize Your Tribute Service
          </h2>
          <p className="text-lg font-sans text-foreground-muted mb-10">
            Choose from common service types or create your own
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Memorial Service',
              'Bhog and Ardas',
              'Tehrvi',
              'Funeral Service',
              'Celebration of Life',
              'Custom Service'
            ].map((service, index) => (
              <span
                key={index}
                className="px-5 py-2 bg-white border border-border rounded-full text-sm font-sans text-foreground shadow-sm"
              >
                {service}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="w-12 h-12 mx-auto mb-6 opacity-90" strokeWidth={1.5} />
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight mb-6">
            {currentUser ? 'Create Another Tribute' : 'Create a Lasting Tribute'}
          </h2>
          <p className="text-lg font-sans mb-8 opacity-90">
            Honor your loved one's memory and bring family together to share their stories.
          </p>
          <Button 
            onClick={handleStartTribute}
            data-testid="cta-start-tribute-btn"
            className="bg-white text-primary hover:bg-gray-100 rounded-full px-10 py-4 text-lg font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
          >
            {currentUser ? 'Set Up New Tribute' : 'Get Started - It\'s Free'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <TreePine className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="text-xl font-serif font-semibold text-foreground">Sarmoh</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/terms" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center pt-6 border-t border-border">
            <p className="text-sm font-sans text-muted-foreground">
              © 2026 TLML Mediatech Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
